/**
 * Combobox — typeahead/autocomplete select (Quick 260614-ut1 / #9).
 *
 * A Popover + cmdk Command combobox: the dispatcher types to filter, instead of
 * scrolling hundreds of operators/sites in a native <select>. Used by the New
 * Job modal for Producer and Site.
 *
 * ── Two filtering modes (ClickUp 86bc7quft) ─────────────────────────────────
 * By DEFAULT cmdk does the substring filtering itself, on the visible label, over
 * whatever `options` it was handed. Every caller but one uses that.
 *
 * That default cannot express a server-side search, and not only for paging
 * reasons: cmdk matches the LABEL, so a well found by API number (label
 * "AB TRUST 28-27-12", query "3500322450") is fetched correctly by the server and
 * then dropped on the floor by cmdk before it can render. Passing
 * `shouldFilter={false}` + `onSearchChange` hands filtering to the caller, which is
 * what the Site picker needs.
 *
 * `shouldFilter` defaults to true and `onSearchChange` is optional, so callers that
 * do not opt in behave exactly as before.
 */

import * as React from 'react';
import { ChevronsUpDown, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from 'src/components/ui/command';

export interface ComboboxOption {
  readonly value: string;
  readonly label: string;
}

export interface ComboboxProps {
  readonly options: ReadonlyArray<ComboboxOption>;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyText?: string;
  readonly disabled?: boolean;
  readonly id?: string;
  /**
   * Called on every keystroke in the search box. Supply this when the OPTIONS are
   * produced by a server query rather than being a fixed local list. Pair it with
   * `shouldFilter={false}` — otherwise cmdk will filter your server results again,
   * by label, and silently discard any match the label does not contain.
   */
  readonly onSearchChange?: (query: string) => void;
  /**
   * Let cmdk substring-filter `options` by label. Default `true` (the historical
   * behaviour). Set false when the caller has already filtered.
   */
  readonly shouldFilter?: boolean;
  /** Show a "searching…" hint in place of the empty state while a query is in flight. */
  readonly loading?: boolean;
  /**
   * Rendered under the list, and ONLY when the list has options (86bc84c5y).
   * Used for a truncation notice — a result capped by the server is indistinguishable
   * from a complete one unless something says so. But "showing the first N" printed
   * directly beneath "no matches" tells the user two opposite things at once, so the
   * empty state owns that case and must say the capping itself (see NewJobModal's
   * emptyText).
   */
  readonly footer?: React.ReactNode;
}

const triggerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  minHeight: '40px',
  padding: '8px 10px',
  fontSize: '14px',
  textAlign: 'left',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  disabled = false,
  id,
  onSearchChange,
  shouldFilter = true,
  loading = false,
  footer,
}: ComboboxProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const selected = options.find((o) => o.value === value);

  // Clear the query when the popover closes so reopening does not show the previous
  // search's (server-fetched) result set as though it were the full list.
  React.useEffect(() => {
    if (!open && search !== '') {
      setSearch('');
      onSearchChange?.('');
    }
    // onSearchChange is a caller callback; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-expanded={open}
          style={{ ...triggerStyle, opacity: disabled ? 0.5 : 1 }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: selected ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
            }}
          >
            {selected?.label ?? placeholder}
          </span>
          <ChevronsUpDown size={14} aria-hidden="true" style={{ flexShrink: 0, opacity: 0.6 }} />
        </button>
      </PopoverTrigger>
      <PopoverContent style={{ width: '320px', padding: 0 }} align="start">
        <Command shouldFilter={shouldFilter}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={(q: string): void => {
              setSearch(q);
              onSearchChange?.(q);
            }}
          />
          <CommandList>
            <CommandEmpty>{loading ? 'Searching…' : emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={(): void => {
                    onChange(opt.value === value ? '' : opt.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    size={14}
                    aria-hidden="true"
                    style={{
                      marginRight: '8px',
                      opacity: opt.value === value ? 1 : 0,
                    }}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {/* 86bc84c5y — never a cap notice under an empty list; CommandEmpty is
              showing instead, and the two together contradict each other. */}
          {options.length > 0 ? footer : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
